import { Suspense, useEffect, useRef, type ReactNode } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useLocation } from "react-router";
import { Notification } from "@/components/admin/notification";
import { Error } from "@/components/admin/error";
import { Skeleton } from "@/components/ui/skeleton";

import { DataImportProvider } from "../dataImport/DataImportProvider";
import { useConfigurationLoader } from "../root/useConfigurationLoader";
import { SplashIdentityGate } from "../splash/SplashGates";
import { AppSidebar } from "./AppSidebar";
import { DeskShellProvider, useDeskShell } from "./desk/DeskShellContext";
import Header from "./Header";

/**
 * Desktop shell: the sidebar is the app background and the content is a white
 * sheet floating on its right, scrolling on its own (desk-shell.css).
 */
const DeskShell = ({ children }: { children: ReactNode }) => {
  const { collapsed } = useDeskShell();
  const { pathname } = useLocation();
  const sheetRef = useRef<HTMLDivElement>(null);

  // The sheet is the scroll container, so reset it on navigation (ra-core
  // only scrolls the window).
  useEffect(() => {
    sheetRef.current?.scrollTo({ top: 0 });
  }, [pathname]);

  return (
    <div
      className="desk-shell"
      data-splash-reveal
      data-collapsed={collapsed ? "true" : undefined}
    >
      <AppSidebar />
      <div className="desk-sheet" ref={sheetRef}>
        <Header />
        <main className="desk-main" id="main-content">
          <ErrorBoundary FallbackComponent={Error}>
            <Suspense
              fallback={<Skeleton className="h-12 w-12 rounded-full" />}
            >
              {children}
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
};

export const Layout = ({ children }: { children: ReactNode }) => {
  useConfigurationLoader();
  return (
    <DataImportProvider>
      <DeskShellProvider>
        <DeskShell>{children}</DeskShell>
        <Notification />
        <SplashIdentityGate />
      </DeskShellProvider>
    </DataImportProvider>
  );
};
